import { Link } from 'react-router-dom';
import { Crown, Gift, Trophy } from 'lucide-react';
import type { StaffTicketCountsResponse } from '../../types/staff';
import { StaffSection } from './StaffSection';
import { StaffHonourList } from './StaffHonourBadge';
import type { StaffHonour } from '../../utils/staffTickets';
import {
  currentTicketMonth,
  describeRaffleStanding,
  findTicketPerson,
  formatTicketMonth,
  personHonours,
  personTicketRank,
  raffleStandingShort,
  ticketSeries,
} from '../../utils/staffTickets';
import { cn } from '../../utils/cn';

interface StaffTicketsCardProps {
  data: StaffTicketCountsResponse;
  discordId: string;
}

const leaderboardLink = (
  <Link to="/staff/tickets" className="glass-button-sm inline-flex items-center gap-1.5">
    <Trophy className="w-3.5 h-3.5" aria-hidden="true" /> Leaderboard
  </Link>
);

const monthList = (list: StaffHonour[]) => list.map((h) => formatTicketMonth(h.month)).join(', ');

/**
 * Tickets closed per month for one member (six months), this month's rank, and their MVP /
 * raffle honours — the numbers behind the bot's /ticketcount command.
 */
export const StaffTicketsCard = ({ data, discordId }: StaffTicketsCardProps) => {
  const person = findTicketPerson(data, discordId);
  const current = currentTicketMonth(data);

  if (!person || !current) {
    return (
      <StaffSection title="Tickets" icon={Trophy} actions={leaderboardLink}>
        <p className="text-xs text-glass-500">
          No ticket history for this member in the last six months — only the Administrator, Moderator and Helper roles (and the bot) count tickets.
        </p>
      </StaffSection>
    );
  }

  const series = ticketSeries(data, person);
  const honours = personHonours(person);
  const mvp = honours.filter((h) => h.kind === 'mvp');
  const raffle = honours.filter((h) => h.kind === 'raffle');
  const thisMonth = person.months[current.month] ?? null;
  const rank = personTicketRank(data, discordId, current.month);
  const max = Math.max(1, ...series.map((p) => (p.standing?.known ? p.standing.tickets : 0)));

  const facts: { label: string; value: string | number; sub: string; tone?: string }[] = [
    {
      label: 'This month',
      value: thisMonth?.known ? thisMonth.tickets : '—',
      sub: rank ? `#${rank.rank} of ${rank.of} · ${formatTicketMonth(current.month)}` : `${formatTicketMonth(current.month)} · in progress`,
    },
    { label: 'Six months', value: person.total_known_tickets, sub: 'known months only' },
    { label: 'MVP', value: mvp.length, sub: mvp.length ? monthList(mvp) : 'most tickets in a month', tone: mvp.length ? 'text-amber-300' : undefined },
    { label: 'Raffle wins', value: raffle.length, sub: raffle.length ? monthList(raffle) : 'drawn from the runners-up', tone: raffle.length ? 'text-purple-300' : undefined },
  ];

  return (
    <StaffSection title="Tickets" icon={Trophy} actions={leaderboardLink}>
      <dl className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
        {facts.map((f) => (
          <div key={f.label} className="rounded-lg bg-glass-100/5 border border-glass-200/10 px-3 py-2 min-w-0">
            <dt className="text-[10px] uppercase tracking-[0.14em] text-glass-500">{f.label}</dt>
            <dd className={cn('text-lg font-bold mt-0.5 tabular-nums leading-tight', f.tone ?? 'text-white')}>{f.value}</dd>
            <dd className="text-[11px] text-glass-500 truncate" title={f.sub}>
              {f.sub}
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-4 grid gap-2" style={{ gridTemplateColumns: `repeat(${series.length}, minmax(0, 1fr))` }} role="img" aria-label="Tickets closed per month">
        {series.map(({ month, standing }) => {
          const known = !!standing?.known;
          const tickets = known && standing ? standing.tickets : 0;
          const live = month.month === current.month;
          const title = `${formatTicketMonth(month.month, 'long')} — ${known ? `${tickets} ticket${tickets === 1 ? '' : 's'}` : 'no record'}${standing ? ` · ${describeRaffleStanding(standing)}` : ''}`;
          return (
            <div key={month.month} className="flex flex-col items-center gap-1 min-w-0" title={title}>
              <span className={cn('text-[11px] font-mono tabular-nums leading-none', known ? 'text-white' : 'text-glass-500')}>{known ? tickets : '?'}</span>
              <div className={cn('relative w-full h-20 flex items-end rounded-md', live ? 'bg-accent-500/10' : 'bg-glass-100/5')}>
                {known ? (
                  <div
                    className={cn(
                      'w-full rounded-t-[4px] transition-all duration-500',
                      live
                        ? 'bg-gradient-to-t from-accent-500 to-accent-300 shadow-[0_0_14px_rgb(var(--accent-t-400-rgb)/0.5)]'
                        : standing?.is_mvp
                          ? 'bg-gradient-to-t from-amber-500/80 to-amber-300/80'
                          : 'bg-gradient-to-t from-accent-600/70 to-accent-400/70',
                    )}
                    style={{ height: `${tickets > 0 ? Math.max(6, (tickets / max) * 100) : 0}%` }}
                  />
                ) : (
                  <div className="absolute inset-1 rounded-md border border-dashed border-glass-200/20" aria-hidden="true" />
                )}
                {standing?.is_mvp && <Crown className="absolute top-1 right-1 w-3 h-3 text-amber-300" aria-hidden="true" />}
                {standing?.is_raffle_winner && <Gift className="absolute top-1 left-1 w-3 h-3 text-purple-300" aria-hidden="true" />}
              </div>
              <span className={cn('text-[10px] font-mono uppercase tracking-wide', live ? 'text-accent-300' : 'text-glass-500')}>{formatTicketMonth(month.month, 'name')}</span>
              <span className="hidden sm:block text-[10px] text-glass-500 truncate max-w-full">{standing ? raffleStandingShort(standing) : ''}</span>
            </div>
          );
        })}
      </div>

      {honours.length > 0 && <StaffHonourList honours={honours} max={6} className="mt-3" />}
      <p className="mt-3 text-[11px] text-glass-500 leading-relaxed">
        Same numbers as the bot's /ticketcount. Each month's top closer is the MVP and sits out the raffle; everyone else's tickets earn raffle entries and one winner is drawn when the month is finalized.
      </p>
    </StaffSection>
  );
};
